// Team-member dashboard — a per-user view of profile, performance/activity, and the clients
// assigned to them. Admins (MANAGE_TEAM) can view anyone and edit assignments; a member sees
// their own. Rep metrics come from the analytics engine (their book); staff shows activity.
import { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import PageHeader from '../components/PageHeader.jsx';
import { fetchTeamMember, setMemberAssignments, updateMemberProfile } from '../api/team.js';
import { fetchCompanies } from '../api/companies.js';
import { setCommissionRate, setCommissionCurrency, syncBeneficiaries } from '../api/commissions.js';
import { fmtMoney } from '../lib/matrix.js';
import { storeShort } from '../lib/stores.js';
import { useAuthStore } from '../stores/authStore.js';
import { can, CAP } from '../lib/access.js';

const ROLE_LABEL = { INTERNAL_STAFF: 'Internal staff', REP: 'Wholesale rep' };
const PAYOUT_CURRENCIES = ['AUD', 'USD', 'EUR', 'GBP', 'NZD', 'CAD', 'SGD'];
const TIER_LABEL = { ADMIN: 'Admin', MANAGER: 'Manager', VIEWER: 'Viewer' };
const STORE_ACCESS_LABEL = { A: 'Australia', B: 'International', BOTH: 'All stores' };
const money = (arr) => (arr || []).filter((r) => r.current).map((r) => fmtMoney(r.current, r.currency)).join(' · ') || '—';

// Profile card — read-only, with inline editing of name/phone/location for admins.
function ProfileCard({ user, canEdit, onSaved }) {
  const blank = () => ({ name: user.name || '', phone: user.phone || '', location: user.location || '' });
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => setForm(blank()), [user.id, user.name, user.phone, user.location]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setBusy(true); setError('');
    try {
      await updateMemberProfile(user.id, { name: form.name.trim(), phone: form.phone.trim() || null, location: form.location.trim() || null });
      setEditing(false);
      await onSaved?.();
    } catch (e) {
      setError(e.response?.data?.error?.message || 'Could not save.');
    } finally {
      setBusy(false);
    }
  };
  const inputCls = 'mt-0.5 w-full rounded-md border border-line px-2 py-1 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/30';

  return (
    <div className="rounded-lg border border-line bg-surface p-5 shadow-sm">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-text-primary">Profile</h2>
        {canEdit && !editing && <button onClick={() => setEditing(true)} className="text-xs font-medium text-accent hover:underline">Edit</button>}
      </div>
      {editing ? (
        <div className="mt-3 space-y-2">
          <label className="block"><span className="text-xs text-text-muted">Name</span><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} /></label>
          <label className="block"><span className="text-xs text-text-muted">Phone</span><input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="e.g. +61 400 000 000" className={inputCls} /></label>
          <label className="block"><span className="text-xs text-text-muted">Location</span><input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="e.g. Sydney, AU" className={inputCls} /></label>
          <div className="flex items-center gap-2 pt-1">
            <button onClick={save} disabled={busy || !form.name.trim()} className="rounded-md bg-accent px-3 py-1.5 text-xs font-medium text-white hover:bg-accent-hover disabled:opacity-50">{busy ? 'Saving…' : 'Save'}</button>
            <button onClick={() => { setEditing(false); setError(''); setForm(blank()); }} className="text-xs font-medium text-text-secondary hover:text-text-primary">Cancel</button>
            {error && <span className="text-xs text-error">{error}</span>}
          </div>
          <p className="pt-1 text-[11px] text-text-muted">Email, role &amp; store access are managed in Team &amp; Access.</p>
        </div>
      ) : (
        <dl className="mt-3 grid grid-cols-[7rem_1fr] gap-y-1.5 text-sm">
          <dt className="text-text-muted">Name</dt><dd className="text-text-primary">{user.name || '—'}</dd>
          <dt className="text-text-muted">Email</dt><dd className="text-text-primary">{user.email}</dd>
          <dt className="text-text-muted">Phone</dt><dd className="text-text-primary">{user.phone || '—'}</dd>
          <dt className="text-text-muted">Location</dt><dd className="text-text-primary">{user.location || '—'}</dd>
          <dt className="text-text-muted">Store access</dt><dd className="text-text-primary">{STORE_ACCESS_LABEL[user.storeAccess] || user.storeAccess}</dd>
          <dt className="text-text-muted">Status</dt>
          <dd className="text-text-primary">{user.isActive ? 'Active' : 'Inactive'}{user.registeredAt ? '' : ' · invite pending'}</dd>
          <dt className="text-text-muted">Last login</dt>
          <dd className="text-text-primary">{user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleString() : '—'}</dd>
        </dl>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-lg border border-line bg-surface p-4 shadow-sm">
      <div className="text-xs text-text-muted">{label}</div>
      <div className="mt-1 text-lg font-semibold text-text-primary">{value}</div>
    </div>
  );
}

function AssignmentEditor({ userId, role, assignedIds, onSaved }) {
  const [companies, setCompanies] = useState([]);
  const [sel, setSel] = useState(() => new Set(assignedIds));
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    fetchCompanies().then(setCompanies).catch(() => setCompanies([]));
  }, []);
  useEffect(() => {
    setSel(new Set(assignedIds));
  }, [assignedIds]);

  const shown = useMemo(() => {
    const n = q.trim().toLowerCase();
    return companies.filter((c) => !n || c.name.toLowerCase().includes(n));
  }, [companies, q]);
  const toggle = (id) =>
    setSel((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const save = async () => {
    setBusy(true);
    setMsg('');
    try {
      const r = await setMemberAssignments(userId, [...sel]);
      setMsg(`Saved — ${r.assigned} assigned (${r.added} added, ${r.removed} removed).`);
      onSaved?.();
    } catch (e) {
      setMsg(e.response?.data?.error?.message || 'Could not save.');
    } finally {
      setBusy(false);
    }
  };

  const label = role === 'REP' ? 'Assigned clients (rep)' : 'Owned accounts (CS)';
  return (
    <div className="rounded-lg border border-line bg-surface p-5 shadow-sm">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-text-primary">{label}</h2>
        <span className="text-xs text-text-muted">{sel.size} selected</span>
      </div>
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search companies…"
        className="mt-3 w-full rounded-md border border-line px-3 py-1.5 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/30"
      />
      <div className="mt-2 max-h-72 overflow-y-auto rounded-md border border-line">
        {shown.length === 0 ? (
          <div className="px-3 py-6 text-center text-xs text-text-muted">No companies.</div>
        ) : (
          shown.map((c) => (
            <label key={c.id} className="flex cursor-pointer items-center gap-2 border-b border-line px-3 py-1.5 text-sm last:border-0 hover:bg-surface-raised">
              <input type="checkbox" checked={sel.has(c.id)} onChange={() => toggle(c.id)} className="accent-accent" />
              <span className="flex-1 truncate text-text-primary">{c.name}</span>
              <span className="text-xs text-text-muted">
                {storeShort(c.store?.code)}
                {c.isActive === false ? ' · inactive' : ''}
              </span>
            </label>
          ))
        )}
      </div>
      <div className="mt-3 flex items-center gap-3">
        <button onClick={save} disabled={busy} className="rounded-md bg-accent px-4 py-2 text-sm font-medium text-white hover:bg-accent-hover disabled:opacity-50">
          {busy ? 'Saving…' : 'Save assignments'}
        </button>
        {msg && <span className="text-sm text-text-secondary">{msg}</span>}
      </div>
    </div>
  );
}

// Rep payout config (rate + currency + Airwallex beneficiary) — set right on the rep's page.
function PayoutCard({ user, canEdit, onSaved }) {
  const [rate, setRate] = useState(user.commissionRate ?? '');
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');
  useEffect(() => setRate(user.commissionRate ?? ''), [user.commissionRate]);
  const currency = user.commissionCurrency || 'AUD';
  const linked = !!user.airwallexBeneficiaryId;

  const run = async (key, fn, done) => {
    setBusy(key); setMsg('');
    try { setMsg(done(await fn()) || ''); await onSaved?.(); }
    catch (e) { setMsg(e.response?.data?.error?.message || 'Could not save.'); }
    finally { setBusy(''); }
  };

  return (
    <div className="rounded-lg border border-line bg-surface p-5 shadow-sm">
      <h2 className="text-sm font-semibold text-text-primary">Commission &amp; payout</h2>
      <p className="mt-0.5 text-xs text-text-muted">Their commission rate, the currency they're paid in, and their Airwallex payout contact.</p>
      <dl className="mt-3 grid grid-cols-[8rem_1fr] items-center gap-y-3 text-sm">
        <dt className="text-text-muted">Commission rate</dt>
        <dd className="flex items-center gap-1">
          {canEdit ? (
            <>
              <input type="number" min={0} max={100} step="0.5" value={rate} onChange={(e) => setRate(e.target.value)} className="w-20 rounded-md border border-line px-2 py-1 text-sm outline-none focus:border-accent" />
              <span className="text-xs text-text-muted">%</span>
              {String(rate) !== String(user.commissionRate ?? '') && (
                <button onClick={() => run('rate', () => setCommissionRate(user.id, rate === '' ? null : Number(rate)), () => 'Saved.')} disabled={busy === 'rate'} className="ml-1 rounded border border-line px-2 py-1 text-xs font-medium text-accent hover:bg-surface-raised">
                  {busy === 'rate' ? '…' : 'Set'}
                </button>
              )}
            </>
          ) : (
            <span className="text-text-primary">{user.commissionRate != null ? `${user.commissionRate}%` : '—'}</span>
          )}
        </dd>

        <dt className="text-text-muted">Paid in</dt>
        <dd>
          {canEdit ? (
            <select value={currency} onChange={(e) => run('cur', () => setCommissionCurrency(user.id, e.target.value === 'AUD' ? null : e.target.value), () => 'Saved.')} disabled={busy === 'cur'} className="rounded-md border border-line bg-surface px-2 py-1 text-sm outline-none focus:border-accent disabled:opacity-60">
              {PAYOUT_CURRENCIES.map((c) => (<option key={c} value={c}>{c}</option>))}
            </select>
          ) : (
            <span className="text-text-primary">{currency}</span>
          )}
          <span className="ml-2 text-xs text-text-muted">earnings across currencies convert to this</span>
        </dd>

        <dt className="text-text-muted">Airwallex</dt>
        <dd className="flex flex-wrap items-center gap-2">
          {linked ? (
            <span className="rounded-full bg-success-light px-2 py-0.5 text-xs font-medium text-success">Beneficiary linked</span>
          ) : (
            <span className="text-xs text-text-muted">Not linked — add a transfer contact in Airwallex with {user.email}</span>
          )}
          {canEdit && (
            <button onClick={() => run('sync', () => syncBeneficiaries(), (r) => (r.linked != null ? `Synced — ${r.linked}/${r.reps} reps linked.` : 'Synced.'))} disabled={busy === 'sync'} className="rounded-md border border-line px-2.5 py-1 text-xs font-medium text-text-secondary hover:bg-surface-raised disabled:opacity-50">
              {busy === 'sync' ? 'Syncing…' : 'Sync Airwallex'}
            </button>
          )}
        </dd>
      </dl>
      {msg && <p className="mt-2 text-xs text-text-secondary">{msg}</p>}
    </div>
  );
}

export default function TeamMember() {
  const { userId } = useParams();
  const viewer = useAuthStore((s) => s.user);
  const isAdmin = can(viewer, CAP.MANAGE_TEAM);
  const canEditPayout = can(viewer, CAP.MANAGE_PAYMENTS); // admins + managers set rep payout
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  const load = () =>
    fetchTeamMember(userId)
      .then(setData)
      .catch((e) => setError(e.response?.data?.error?.message || 'Could not load this dashboard.'));
  useEffect(() => {
    setData(null);
    setError('');
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  if (error) return (<><PageHeader title="Team member" /><div className="rounded-lg border border-line bg-surface p-8 text-center text-sm text-text-muted">{error}</div></>);
  if (!data) return <div className="text-sm text-text-muted">Loading…</div>;

  const { user, role, metrics } = data;
  const isRep = role === 'REP';
  const assignedIds = (isRep ? data.assignedCompanies : data.ownedCompanies)?.map((c) => c.id) || [];

  return (
    <>
      {isAdmin && (
        <Link to="/settings" className="mb-3 inline-flex items-center gap-1 text-sm text-text-secondary hover:text-text-primary">
          ← Back to Team &amp; Access
        </Link>
      )}
      <PageHeader title={user.name || user.email} subtitle={`${ROLE_LABEL[role] || role}${isRep ? '' : ` · ${TIER_LABEL[user.accessLevel] || user.accessLevel}`} · last 90 days`} />

      {/* Profile */}
      <div className="mb-4 grid gap-4 md:grid-cols-2">
        <ProfileCard user={user} canEdit={isAdmin} onSaved={load} />

        {/* Metrics */}
        {isRep ? (
          <div className="grid grid-cols-2 gap-3">
            <Stat label="Revenue (approved)" value={money(metrics?.headline?.revenue)} />
            <Stat label="Orders" value={metrics?.headline?.orders?.current ?? 0} />
            <Stat label="Units" value={metrics?.headline?.units?.current ?? 0} />
            <Stat label="AOV" value={money(metrics?.headline?.aov)} />
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            <Stat label="Orders created" value={metrics?.ordersCreated ?? 0} />
            <Stat label="Orders approved" value={metrics?.approvals ?? 0} />
            <Stat label="Orders rejected" value={metrics?.rejections ?? 0} />
            <Stat label="Lookbooks sent" value={metrics?.lookbooks ?? 0} />
          </div>
        )}
      </div>

      {/* Rep commission & payout config */}
      {isRep && (
        <div className="mb-4">
          <PayoutCard user={user} canEdit={canEditPayout} onSaved={load} />
        </div>
      )}

      {/* Rep top clients / products */}
      {isRep && (metrics?.topClients?.length > 0 || metrics?.topProducts?.length > 0) && (
        <div className="mb-4 grid gap-4 md:grid-cols-2">
          <div className="rounded-lg border border-line bg-surface p-5 shadow-sm">
            <h2 className="mb-2 text-sm font-semibold text-text-primary">Top clients</h2>
            {metrics.topClients?.length ? metrics.topClients.slice(0, 6).map((c) => (
              <div key={c.name} className="flex justify-between border-b border-line py-1 text-sm last:border-0">
                <span className="truncate text-text-primary">{c.name}</span>
                <span className="tabular-nums text-text-secondary">{fmtMoney(c.revenue, c.currency)}</span>
              </div>
            )) : <p className="text-xs text-text-muted">No sales in this period.</p>}
          </div>
          <div className="rounded-lg border border-line bg-surface p-5 shadow-sm">
            <h2 className="mb-2 text-sm font-semibold text-text-primary">Top products</h2>
            {metrics.topProducts?.length ? metrics.topProducts.slice(0, 6).map((p) => (
              <div key={p.title} className="flex justify-between border-b border-line py-1 text-sm last:border-0">
                <span className="truncate text-text-primary">{p.title}</span>
                <span className="tabular-nums text-text-secondary">{p.units} u</span>
              </div>
            )) : <p className="text-xs text-text-muted">No sales in this period.</p>}
          </div>
        </div>
      )}

      {/* Assignments: editable for admins, read-only list otherwise */}
      {/* Admins have every client (a company has a single CS owner, so admin access is a visibility
          rule, not editable per-account assignment) — show it read-only. Reps + manager staff get
          the editable assignment picker. */}
      {isAdmin && !data.ownsAllClients ? (
        <AssignmentEditor userId={user.id} role={role} assignedIds={assignedIds} onSaved={load} />
      ) : (
        <div className="rounded-lg border border-line bg-surface p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-text-primary">
              {data.ownsAllClients ? 'All clients' : isRep ? 'Your assigned clients' : 'Accounts you own'}
            </h2>
            {data.ownsAllClients && <span className="rounded-full bg-accent-light px-2 py-0.5 text-[10px] font-medium text-text-primary">Admin · full access</span>}
          </div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {assignedIds.length === 0 ? (
              <p className="text-xs text-text-muted">None yet.</p>
            ) : (
              (isRep ? data.assignedCompanies : data.ownedCompanies).map((c) => (
                <span key={c.id} className="rounded-full bg-surface-raised px-2.5 py-0.5 text-xs text-text-secondary">{c.name}</span>
              ))
            )}
          </div>
        </div>
      )}
    </>
  );
}
